import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  clampPhotoCenterToCanvas,
  computePhotoPlacementSize,
  getPasteboardPlacementBounds,
  topLeftFromCenter,
} from '@/lib/photos/photo-placement-geometry'
import type { BuildPlacedFillShapeInput } from './types/build-placed-fill-shape-input'

/**
 * A tray item (photo, ClikArt) dropped directly onto the canvas — not onto an existing shape —
 * creates a real borderless `kindId 1` rectangle sized to the item, with the item as its
 * `photoFill`: the same mechanism as dropping it into a hand-drawn shape, rather than a distinct
 * "image element" (kindId 4). It persists via `buildRectangleElement`'s `content.image` (matching
 * legacy NSD's `<PE><RR><XX><IM>`). `offsetX`/`offsetY: 0` and `scale: 1` mean no crop: the
 * rectangle's aspect ratio already matches the item's own (via `computePhotoPlacementSize`).
 *
 * `buildPlacedPhotoShape` and `buildPlacedClikArtShape` supply only the `buildFill` callback.
 */
export function buildPlacedFillShape(input: BuildPlacedFillShapeInput): ShapeData {
  const size = input.size ?? computePhotoPlacementSize(input.intrinsicWidth, input.intrinsicHeight)
  const bounds = getPasteboardPlacementBounds(input.canvasWidth, input.canvasHeight)
  const { centerX, centerY } = clampPhotoCenterToCanvas(
    input.centerX,
    input.centerY,
    size.width,
    size.height,
    bounds
  )
  const { x, y } = topLeftFromCenter(centerX, centerY, size.width, size.height)
  const naturalSize =
    input.intrinsicWidth && input.intrinsicHeight
      ? { width: input.intrinsicWidth, height: input.intrinsicHeight }
      : undefined

  return {
    id: input.objectId,
    type: 'square',
    kindId: 1,
    x,
    y,
    width: size.width,
    height: size.height,
    fill: 'transparent',
    stroke: 'transparent',
    strokeWidth: 0,
    roundedCorner: 0,
    opacity: 1,
    photoFill: input.buildFill(naturalSize),
  }
}
